import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { jobPhotoRecordsOrderCmpFn, jobServiceChecklistQuestions } from 'src/utils/helpers';
import { notification } from 'src/components/Notification';
import { toSimpleDateString } from 'src/utils/format/date';
import { useGetMobileDataGeocoordinates, useGetMobilePhotoRecordData, useUpdateJobAuditSiteData } from 'src/hooks/job';
import AddMorePhotos from 'src/components/JobMobileDataTab/AddMorePhotos';
import ImageGrid from 'src/components/JobMobileDataTab/ImageGrid';
import Button from 'src/components/Button';
import logger from 'src/utils/logger';
import MobilePhotoAttachmentViewer from 'src/components/MobilePhotoAttachmentViewer';
import MobileDataPhotoDownload from 'src/components/JobMobileDataTab/MobileDataPhotoDownload';
import Styled from 'src/components/JobMobileDataTab/JobMobilData.styles';
import PhotoFormPrev from 'src/components/JobMobileDataTab/PhotoFormPrev';
import useGetJobChecklists from 'src/components/JobMobileDataTab/hooks/useGetJobChecklists';

const JobMobileDataTab = ({ isEditableByUser, job, jobChecklists, jobDatasheetResults }) => {
  const [photoRecordsByOrder, setPhotoRecordsByOrder] = useState([]);
  const [photoViewerVisible, setPhotoViewerVisible] = useState(false);
  const [selectedImageToPreview, setSelectedImageToPreview] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [orderedPrimaryOnlyRecords, setOrderedPrimaryOnlyRecords] = useState([]);
  const { photoRecordsData = [], execute: getPhotoRecordsFromDB } = useGetMobilePhotoRecordData(job.id);
  const [checklistQuestions, setChecklistQuestions] = useState([]);

  const updateJobAuditSiteData = useUpdateJobAuditSiteData();
  const mobileDataGeocoordinates = useGetMobileDataGeocoordinates(job.id);
  const { checklists, loading, getChecklists } = useGetJobChecklists(job.id);

  const { t } = useTranslation();

  const showPreviewPhotoFormMessage = useMemo(
    () => orderedPrimaryOnlyRecords.some(record => record.viewInPhotoForm),
    [orderedPrimaryOnlyRecords]);

  const isJobUnderReview = job.status === 'Under review';

  useEffect(() => {
    if (job.id) {
      getPhotoRecordsFromDB();
    }
  }, [job.id]);

  useEffect(() => {
    if (jobChecklists &&
      Array.isArray(jobChecklists) &&
      jobChecklists.length > 0) {
      getChecklists(jobChecklists.map(({ currentVersion }) => currentVersion.id));
    }
  }, [jobChecklists]);

  useEffect(() => {
    if (checklists.length > 0) {
      jobServiceChecklistQuestions(job, checklists)
        .then((result) => {
          setChecklistQuestions(result);
        }).catch(err => {
          logger.error(err);
        });
    }
  }, [checklists]);

  useEffect(() => {
    if (photoRecordsData && photoRecordsData.length) {
      const toSort = [...photoRecordsData];
      const sorted = toSort.sort(jobPhotoRecordsOrderCmpFn)
        .map((photoRecord, index) => {
          const record = { ...photoRecord };
          if (!record.order) {
            record.order = index + 1;
          }
          return record;
        });
      setPhotoRecordsByOrder(sorted);
    }
  }, [photoRecordsData]);

  useEffect(() => {
    if (photoRecordsByOrder && photoRecordsByOrder.length > 0) {
      setOrderedPrimaryOnlyRecords(photoRecordsByOrder.filter(record => record.isPrimary));
    }
  }, [photoRecordsByOrder]);

  const updatedPositionOfRecords = (records) => {
    return records.map((record, index) => {
      return {
        id: record.id,
        order: index + 1 ,
        showFindings: record.showFindings,
        viewInPhotoForm: record.viewInPhotoForm,
        showAltered: record.showAltered,
        alteredFileName: record.alteredFileName,
        alteredAttachmentId: record.alteredAttachmentId
      };
    });
  };

  const isMobileDataEditable = () => {
    if (!isEditableByUser) {
      notification.error(t('MOBILE_TAB_READONLY_FOR_EXTERNAL_USERS'));
      return false;
    }
    if (!isJobUnderReview) {
      notification.error(t('MOBILE_TAB_READONLY_JOB_NOT_UNDER_REVIEW'));
      return false;
    }
    return true;
  };

  const optimisticUpdate = (photoAttachmentId, status, field)=>{
    const changedObjectIndex = orderedPrimaryOnlyRecords.findIndex(o=> o.id === photoAttachmentId);
    if(changedObjectIndex !== -1){
      const changingObject = { ...orderedPrimaryOnlyRecords[changedObjectIndex] };
      changingObject[field] = status;
      const newOrderedPrimaryOnlyRecords =[...orderedPrimaryOnlyRecords];
      newOrderedPrimaryOnlyRecords.splice(changedObjectIndex,1,changingObject)
      setOrderedPrimaryOnlyRecords(newOrderedPrimaryOnlyRecords);
    }
  };

  const includeInPhotoForm = async (
    showFindingsCheck,
    photoAttachmentId,
    photoOrderId,
    status,
    alteredPhotoOptions={}) => {
    if (isMobileDataEditable()) {
      try {
        const photoOrderIdrecords = parseInt(photoOrderId, 10);
        const updateIncludeInphotoRecord = {
          id: photoAttachmentId,
          order: photoOrderIdrecords,
          viewInPhotoForm: status,
          showFindings: showFindingsCheck,
          showAltered: alteredPhotoOptions.showAltered,
          alteredFileName: alteredPhotoOptions.alteredFileName,
          alteredAttachmentId: alteredPhotoOptions.alteredAttachmentId
        };
        optimisticUpdate(photoAttachmentId, status, 'viewInPhotoForm');
        await updateJobAuditSiteData(job.id, { 'photoRecords': updateIncludeInphotoRecord });
      } catch (err) {
        notification.error(t('PHOTO_INCLUSION_IN_FORM_FAILED_ERR_MSG'));
        optimisticUpdate(photoAttachmentId, !status, 'viewInPhotoForm');
      }finally {
        await getPhotoRecordsFromDB();
      }
    }
  };

  const setIsInFindings = async (
    viewInPhotoFormCheck,
    photoAttachmentId,
    photoOrderId,
    status,
    alteredPhotoOptions={}) => {
    if (isMobileDataEditable()) {
      try {
        const photoOrderIdrecords = parseInt(photoOrderId, 10);
        const updateIncludeInphotoRecord = {
          id: photoAttachmentId,
          order: photoOrderIdrecords,
          showFindings: status,
          viewInPhotoForm: viewInPhotoFormCheck,
          showAltered: alteredPhotoOptions.showAltered,
          alteredFileName: alteredPhotoOptions.alteredFileName,
          alteredAttachmentId: alteredPhotoOptions.alteredAttachmentId
        };
        optimisticUpdate(photoAttachmentId, status, 'showFindings');
        await updateJobAuditSiteData(job.id, { 'photoRecords': updateIncludeInphotoRecord });
      } catch (err) {
        optimisticUpdate(photoAttachmentId, !status, 'showFindings');
        notification.error(t('PHOTO_INCLUSION_IN_FINDINGS_FAILED_ERR_MSG'));
      }finally {
        await getPhotoRecordsFromDB();
      }
    }
  };

  const reorder = async (sourceIndex, destinationIndex) => {
    if (isMobileDataEditable()) {
      const reordered = Array.from(orderedPrimaryOnlyRecords);
      const [removed] = reordered.splice(sourceIndex, 1);
      reordered.splice(destinationIndex, 0, removed);

      setOrderedPrimaryOnlyRecords(reordered);

      const reorderRequestDataJson = await updatedPositionOfRecords(reordered);
      const { data } = await updateJobAuditSiteData(job.id, { 'photoRecords': reorderRequestDataJson });

      if (!data.UpdateJobAuditSiteData.success) {
        notification.error(t('MOBILE_TAB_PHOTO_REODERING_FAILED_ERR_MSG'));
        return false;
      }
      await getPhotoRecordsFromDB();
      return data;
    }
    return null;
  };

  const setAlteredImageData = async (showFindingsCheck,viewInPhotoFormCheck, photoOrder, photoAttachmentId, alterOptions={}) => {
    if (isMobileDataEditable()) {
      try {
        const photoOrderInt = parseInt(photoOrder, 10);
        const updateIncludeInPhotoRecord = {
          id: photoAttachmentId,
          order: photoOrderInt,
          viewInPhotoForm: viewInPhotoFormCheck,
          showFindings: showFindingsCheck,
          showAltered: alterOptions.showAltered,
          alteredFileName: alterOptions.alteredFileName,
          alteredAttachmentId: alterOptions.alteredAttachmentId
        };
        await updateJobAuditSiteData(job.id, { 'photoRecords': updateIncludeInPhotoRecord });
      } catch (err) {
        notification.error(t('PHOTO_INCLUSION_IN_FORM_FAILED_ERR_MSG'));
      }finally {
        await getPhotoRecordsFromDB();
      }
    }
  };

  const onImageClicked = (clickedImageId) => {
    setSelectedImageToPreview(
      orderedPrimaryOnlyRecords.findIndex(record => record.id === clickedImageId),
    );
    setPhotoViewerVisible(true);
  };

  const revertEditedImgToOriginal = async (record) =>{
    if (isMobileDataEditable()) {
      try {

        const updateIncludeInPhotoRecord = {
          id: record.id,
          order: parseInt(record.order,10),
          viewInPhotoForm: record.viewInPhotoForm,
          showFindings: record.viewInPhotoForm,
          showAltered: false,
          alteredFileName: null,
          alteredAttachmentId: null
        };
        await updateJobAuditSiteData(job.id, { 'photoRecords': updateIncludeInPhotoRecord });
      } catch (err) {
        notification.error(t('IMAGE_REVERTING_TO_ORIGINAL_FAILED'));
      }finally {
        await getPhotoRecordsFromDB();
      }
    }
  }

  const renderPhotoViewer = () => {
    return <MobilePhotoAttachmentViewer
      allPhotoRecords={photoRecordsData}
      clickedImageIndex={selectedImageToPreview}
      closeHandler={() => setPhotoViewerVisible(false)}
      getPhotoRecordsFromDB={getPhotoRecordsFromDB}
      isEditableByUser={isEditableByUser}
      isJobUnderReview={isJobUnderReview}
      jobChecklistsQuestions={checklistQuestions}
      jobId={job.id}
      jobReportNumber={job.reportNumber}
      photoRecords={orderedPrimaryOnlyRecords}
      revertEditedImgToOriginal={revertEditedImgToOriginal}
      setAlteredImageData={setAlteredImageData}
      tagsConfiguration={job.tagsConfiguration}/>;
  };

  const handleShowPhotoFormPreview = (e) => {
    e.preventDefault();
    setShowPreview(true);
  };

  const renderPhotoFormPreviewMessage = () => {
    return (
      <>
        {t('PHOTO_FORM_PREVIEW_HINT_MSG')}&nbsp;
        <Button onClick={handleShowPhotoFormPreview}>{t('PREVIEW_PHOTO_FORM_BTN_TXT')}</Button>
      </>
    );
  };

  return (
    <>
      <Styled.TopInfoBlock>
        {showPreviewPhotoFormMessage ? renderPhotoFormPreviewMessage() : null}
      </Styled.TopInfoBlock>
      <Styled.Title>{t('FORM_PHOTOS')}</Styled.Title>
      { photoRecordsData && photoRecordsData.length > 0 && <ImageGrid
        allRecords={photoRecordsData}
        includeInPhotoForm={includeInPhotoForm}
        isEditableByUser={isEditableByUser}
        isJobUnderReview={isJobUnderReview}
        openImagePreview={onImageClicked}
        records={orderedPrimaryOnlyRecords}
        reorder={reorder}
        setIsInFindings={setIsInFindings}/>}
      {photoViewerVisible ? renderPhotoViewer() : null}
      {showPreview && (
        <Styled.FormPopup level={0} onClose={() => setShowPreview(false)} showCloseIcon>
          <PhotoFormPrev formPhotoRecords={photoRecordsByOrder.filter(record=>!!record.viewInPhotoForm && record.isPrimary)}
                         job={job}
                         jobDatasheetResults={jobDatasheetResults}/>
        </Styled.FormPopup>
      )}
      <Styled.TopInfoBlock>
        <AddMorePhotos
          getPhotoRecordsFromDB={getPhotoRecordsFromDB}
          isMobileDataEditable={isMobileDataEditable}
          jobId={job.id}
          jobReportNumber={job.reportNumber}/>

        <MobileDataPhotoDownload
          photoRecordsData ={photoRecordsData}
          checklistQuestionsData = {checklistQuestions}
          onClose={() => {}}
        />
      </Styled.TopInfoBlock>

      <Styled.Title u-mt-30>{t('FORM_GEOCOORDINATES')}</Styled.Title>
      <Styled.Table>
        <thead>
          <tr>
            <td>{t('FORM_DATETIME')}</td>
            <td>{t('FORM_LATITUDE')}</td>
            <td>{t('FORM_LONGITUDE')}</td>
          </tr>
        </thead>
        <tbody>
          {mobileDataGeocoordinates &&
            mobileDataGeocoordinates?.data?.JobGeolocations?.map(geocoordinates => {
              return (
                <tr key={geocoordinates.id}>
                  <td>{geocoordinates.capturedOn ? toSimpleDateString(geocoordinates.capturedOn) : 'N/A'}</td>
                  <td>{geocoordinates.lat ? geocoordinates.lat : 'N/A'}</td>
                  <td>{geocoordinates.long ? geocoordinates.long : 'N/A'}</td>
                </tr>
              );
            })}
        </tbody>
      </Styled.Table>
    </>);
};

JobMobileDataTab.propTypes = {
  isEditableByUser: PropTypes.bool,
  job: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    reportNumber: PropTypes.string,
    jobCompanies: PropTypes.arrayOf(
      PropTypes.shape({
        companyTypeId: PropTypes.number,
        company: PropTypes.shape({
          id: PropTypes.number,
          currentVersion: PropTypes.shape({
            name: PropTypes.string,
            companyTypes: PropTypes.arrayOf(PropTypes.shape({})),
            companyAddresses: PropTypes.arrayOf(
              PropTypes.shape({
                currentVersion: PropTypes.shape({
                  address1: PropTypes.string,
                  countryId: PropTypes.number,
                }),
              }),
            ),
          }),
        }),
      }),
    ),
    status: PropTypes.string,
    tagsConfiguration: PropTypes.object
  }).isRequired,
  jobChecklists: PropTypes.array,
  jobDatasheetResults: PropTypes.array,
};

JobMobileDataTab.defaultProps={
  isEditableByUser: false,
  jobDatasheetResults: []
};

export default React.memo(JobMobileDataTab);
